<script setup>
import { computed, inject, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { PrimaryButton, DatePicker, useSnackbarStore } from '@es-bill/es-bill-common-components';
import { useCommunitiesStore } from '@/stores/communitiesStore';
import ServiceFeesTab from '@/components/service-fees/ServiceFeesTab.vue';

const $log = inject('log');
const { t } = useI18n();
const route = useRoute();
const snackbarStore = useSnackbarStore();
const communitiesStore = useCommunitiesStore();

const ecid = computed(() => route.params.ecid);

const tab = ref('serviceFees');
const form = ref(null);

// the form keeps dates as YYYY-MM-DD strings, the DatePicker works on Date objects;
// noon keeps the calendar day stable between the local zone and the display zone
function toDateInput(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().substring(0, 10);
}

function dateFromForm(value) {
  return value ? new Date(value + 'T12:00:00') : null;
}

function dateToForm(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

async function loadData() {
  if (!communitiesStore.allCommunities.length)
    await communitiesStore.fetchCommunities().catch(() => {});

  const community = communitiesStore.getCommunityByEcid(ecid.value);
  if (community) {
    form.value = JSON.parse(JSON.stringify(community));
    form.value.dateStart = toDateInput(community.dateStart);
    form.value.dateEnd = toDateInput(community.dateEnd);
  }
}
loadData();
watch(ecid, loadData);

const dateStartPicker = computed({
  get: () => dateFromForm(form.value?.dateStart),
  set: (v) => { form.value.dateStart = dateToForm(v); },
});

const dateEndPicker = computed({
  get: () => dateFromForm(form.value?.dateEnd),
  set: (v) => { form.value.dateEnd = dateToForm(v); },
});

async function onSave() {
  try {
    // the update call sets only sent fields, an empty date stays out of the
    // payload since the schema refuses null dates (no removal via this view)
    const { dateStart, dateEnd, ...payload } = form.value;
    if (dateStart) payload.dateStart = dateStart;
    if (dateEnd) payload.dateEnd = dateEnd;
    const updated = await communitiesStore.updateCommunityByEcid(ecid.value, payload);
    form.value = JSON.parse(JSON.stringify(updated));
    form.value.dateStart = toDateInput(updated.dateStart);
    form.value.dateEnd = toDateInput(updated.dateEnd);
    snackbarStore.showSnackbar(t('saved'), '', 'Success', 4000, []);
  } catch (error) {
    $log?.warn('save community failed', error);
    if (error?.response?.status === 409)
      snackbarStore.showSnackbar(t('communityDetail.versionConflict'), '', 'Error', 10000, []);
    else
      snackbarStore.showSnackbar(t('connectionProblem'), '', 'Error', 10000, []);
  }
}

function onServiceFeesUpdate(fees) {
  form.value.serviceFees = fees;
  onSave();
}
</script>

<template>
  <v-container v-if="form">
    <div class="d-flex justify-space-between align-center mb-4">
      <p class="textHeadline">{{ form.name }} ({{ form.ecid }})</p>
      <PrimaryButton :text="t('save')" @click="onSave" />
    </div>

    <v-tabs v-model="tab" class="mb-4">
      <v-tab value="serviceFees">{{ t('serviceFees.title') }}</v-tab>
      <v-tab value="masterData">{{ t('communityDetail.tabMasterData') }}</v-tab>
      <v-tab value="admin">{{ t('communityDetail.tabAdmin') }}</v-tab>
    </v-tabs>

    <v-window v-model="tab">
      <v-window-item value="serviceFees">
        <ServiceFeesTab
          :service-fees="form.serviceFees || []"
          @update:service-fees="onServiceFeesUpdate"
        />
      </v-window-item>

      <v-window-item value="masterData" class="pt-2">
        <v-row dense>
          <v-col cols="6">
            <v-text-field :model-value="form.ecid" label="ECID" bg-color="white" variant="outlined" density="compact" readonly disabled />
          </v-col>
          <v-col cols="3">
            <v-text-field :model-value="form.ecn" label="ECN" bg-color="white" variant="outlined" density="compact" readonly disabled />
          </v-col>
          <v-col cols="3">
            <v-text-field :model-value="form.ecnDso || ''" label="ECN DSO" bg-color="white" variant="outlined" density="compact" readonly disabled />
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="6">
            <v-text-field v-model="form.name" :label="t('name')" bg-color="white" variant="outlined" density="compact" />
          </v-col>
          <v-col cols="3">
            <v-text-field :model-value="form.communityType" :label="t('communityType')" bg-color="white" variant="outlined" density="compact" readonly disabled />
          </v-col>
          <v-col cols="3">
            <v-text-field :model-value="form.distributionModel || ''" :label="t('communityDetail.distributionModel')" bg-color="white" variant="outlined" density="compact" readonly disabled />
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="6">
            <v-text-field v-model="form.status" :label="t('communityDetail.status')" bg-color="white" variant="outlined" density="compact" />
          </v-col>
          <v-col cols="6">
            <v-text-field v-model="form.accountingReference" :label="t('communityProviders.accountingReference')" bg-color="white" variant="outlined" density="compact" />
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="6">
            <v-text-field v-model="form.transformerReference" :label="t('communityDetail.transformerReference')" bg-color="white" variant="outlined" density="compact" />
          </v-col>
          <v-col cols="6">
            <v-text-field v-model="form.substationReference" :label="t('communityDetail.substationReference')" bg-color="white" variant="outlined" density="compact" />
          </v-col>
        </v-row>
      </v-window-item>

      <v-window-item value="admin" class="pt-2">
        <p class="text-subtitle-2 mb-2">{{ t('communityDetail.datesTitle') }}</p>
        <v-row dense>
          <v-col cols="6">
            <DatePicker v-model="dateStartPicker" :placeholder="t('serviceFees.dateStart')" :has-day-of-week="false" />
          </v-col>
          <v-col cols="6">
            <DatePicker v-model="dateEndPicker" :placeholder="t('serviceFees.dateEnd')" :has-day-of-week="false" />
          </v-col>
        </v-row>
        <p class="text-body-2 mt-2">{{ t('communityDetail.datesHint') }}</p>
      </v-window-item>
    </v-window>
  </v-container>
</template>
